<!-- Releases.svelte — owner-only desktop release control plane: which version the
     update feed actually serves, which builds are STAGED (uploaded but withheld until
     the Chrome Web Store extension catches up), promotion history, and one-click
     rollback when a release is bad. -->
<script lang="ts">
  import { onMount } from "svelte";
  import { RotateCcw, Check, RefreshCw, AlertTriangle } from "lucide";
  import { Icon, Button, dialogs } from "@price-check/ui";
  import type { Api, ReleaseFeed, ReleaseOs } from "../lib/api";
  import { ApiError } from "../lib/api";
  import Card from "../lib/ui/Card.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import Pill from "../lib/ui/Pill.svelte";
  import StagedRelease from "./releases/StagedRelease.svelte";

  let { api }: { api: Api } = $props();

  let feed = $state<ReleaseFeed | null>(null);
  let status = $state<"loading" | "ready" | "error">("loading");
  let error = $state("");
  let busy = $state<string | null>(null); // version being activated

  const OS_LABEL: Record<ReleaseOs, string> = { windows: "Windows", mac: "macOS", linux: "Linux" };
  const osLabel = (os: ReleaseOs) => OS_LABEL[os];

  // The version every platform is currently serving (Windows is the canonical feed;
  // fall back to whichever platform has one). null if nothing is published.
  const activeVersion = $derived(feed?.activeVersion ?? null);
  // The newest OLDER version — the rollback target. A newer one is staged, not previous.
  const previous = $derived(feed?.versions.find((v) => v.state === "previous") ?? null);

  async function load() {
    status = "loading";
    error = "";
    try {
      feed = await api.releases();
      status = "ready";
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load the release feed.";
      status = "error";
    }
  }

  onMount(load);

  async function activate(version: string, promoting = false) {
    const store = feed?.storeExtVersion ? `v${feed.storeExtVersion}` : "unknown";
    const ok = await dialogs.confirm({
      title: promoting ? `Offer ${version} to customers?` : `Make ${version} the active version?`,
      message: promoting
        ? `This publishes the update feed for ${version} — every client downloads and installs it ` +
          `on its next quit, and it cannot be taken back (rolling the feed back force-downgrades ` +
          `the fleet). Last recorded live store extension: ${store}. Only promote when the store ` +
          `has caught up; 'pnpm release:promote' checks that for you.`
        : `All platforms with a ${version} build will start serving it. Desktop clients on a ` +
          `newer version downgrade to it on their next launch; clients already on ${version} stay put.`,
      confirmLabel: promoting ? `Promote ${version}` : `Switch to ${version}`,
      tone: "danger",
    });
    if (!ok) return;
    busy = version;
    try {
      const res = await api.activateRelease(version, {
        note: promoting ? "promoted from the portal" : "rollback from the portal",
      });
      await load();
      const skipped = res.skipped.length
        ? ` (skipped: ${res.skipped.map((s) => OS_LABEL[s.os]).join(", ")})`
        : "";
      await dialogs.alert({
        title: `Now serving ${version}`,
        message: `Updated ${res.updated.join(", ")}.${skipped}`,
      });
    } catch (e) {
      await dialogs.alert({
        title: "Couldn't switch version",
        message: e instanceof ApiError ? e.message : "Activation failed.",
      });
    } finally {
      busy = null;
    }
  }
</script>

<div class="flex max-w-[720px] flex-col gap-5">
  {#if status === "loading"}
    <div class="text-sm text-muted">Loading releases…</div>
  {:else if status === "error"}
    <Banner tone="danger">{error}</Banner>
    <Button variant="outline" size="sm" class="self-start" onclick={load}>
      <Icon icon={RefreshCw} size={14} /> Retry
    </Button>
  {:else if feed}
    <!-- Active + quick rollback -->
    <Card
      title="Active version"
      subtitle="What the desktop auto-update feed is currently serving to clients."
    >
      {#snippet actions()}
        <Button variant="outline" size="sm" onclick={load} title="Refresh">
          <Icon icon={RefreshCw} size={14} />
        </Button>
      {/snippet}

      {#if activeVersion}
        <div class="flex flex-wrap items-center gap-x-6 gap-y-1.5">
          {#each ["windows", "mac", "linux"] as os (os)}
            <div class="text-sm">
              <span class="text-muted">{OS_LABEL[os as ReleaseOs]}:</span>
              <span class="font-semibold text-ink">{feed.active[os as ReleaseOs] ?? "—"}</span>
            </div>
          {/each}
        </div>

        {#if previous}
          <div class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3.5 py-3">
            <div class="text-sm">
              <div class="font-semibold text-ink">Roll back to {previous.version}</div>
              <div class="text-muted">The previous build — use if {activeVersion} is bad.</div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy !== null}
              onclick={() => activate(previous.version)}
            >
              <Icon icon={RotateCcw} size={14} />
              {busy === previous.version ? "Switching…" : `Roll back to ${previous.version}`}
            </Button>
          </div>
        {:else}
          <p class="mt-3 text-sm text-muted">
            No previous version is on the volume to roll back to.
          </p>
        {/if}
      {:else}
        <p class="text-sm text-muted">No version is published yet.</p>
      {/if}
    </Card>

    <!-- Staged but withheld — the Chrome Web Store gate -->
    <StagedRelease {feed} {busy} label={osLabel} onpromote={(v) => activate(v, true)} />

    <!-- All versions -->
    <Card
      title="Available versions"
      subtitle="Builds still on the server volume. Older ones are pruned automatically on each release (current + previous kept)."
    >
      {#if feed.versions.length === 0}
        <p class="text-sm text-muted">Nothing published.</p>
      {:else}
        <div class="flex flex-col divide-y divide-border">
          {#each feed.versions as v (v.version)}
            {@const isActive = v.state === "active"}
            <div class="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-semibold text-ink">{v.version}</span>
                  {#if isActive}
                    <span class="inline-flex items-center gap-1 rounded-full border border-success-border bg-success-bg px-2 py-0.5 text-2xs font-semibold text-success">
                      <Icon icon={Check} size={11} /> Active
                    </span>
                  {:else if v.state === "held"}
                    <Pill tone="amber">withheld</Pill>
                  {/if}
                </div>
                <div class="text-xs text-muted">
                  {v.platforms.length
                    ? v.platforms.map((p) => OS_LABEL[p]).join(" · ")
                    : "no installers"}
                </div>
              </div>
              {#if !isActive}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy !== null || v.platforms.length === 0}
                  onclick={() => activate(v.version, v.state === "held")}
                >
                  {busy === v.version
                    ? "Switching…"
                    : v.state === "held"
                      ? "Promote"
                      : "Make active"}
                </Button>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </Card>

    <!-- Promotion audit trail -->
    <Card
      title="Promotions"
      subtitle="Every time a build was offered to customers, and the live store extension version that justified it."
    >
      {#if feed.promotions.length === 0}
        <p class="text-sm text-muted">No promotion has been recorded yet.</p>
      {:else}
        <div class="flex flex-col divide-y divide-border">
          {#each feed.promotions as p (p.id)}
            <div class="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm first:pt-0 last:pb-0">
              <span class="font-semibold text-ink">v{p.version}</span>
              <span class="text-muted">
                store ext {p.store_ext_version ? `v${p.store_ext_version}` : "unknown"}
                · {p.actor ?? "unknown"} · {new Date(p.created_at).toLocaleString()}
              </span>
            </div>
          {/each}
        </div>
      {/if}
    </Card>

    <div class="flex items-start gap-2 text-xs text-muted">
      <Icon icon={AlertTriangle} size={14} class="mt-0.5 shrink-0" />
      <span>
        Promoting rewrites the update manifests — the only thing that offers a build to anyone.
        It cannot be undone by withholding: clients auto-download and install on next quit, and
        pointing the feed back at an older version force-downgrades the fleet (builds from 0.1.7+
        allow downgrades). Withhold first, promote once.
      </span>
    </div>
  {/if}
</div>
